import React from "react";
// Legacy flat-sheet renderer for slot kinds that don't go through the
// shared DrawTemplate pipeline (currently `flat-legacy`: magnets, etc.).
// Renders the sheet as a positioned div with a white background and each
// cell as a positioned <img>. The parent <Slot> wrapper handles slot-level
// rotation + perspective via CSS transforms; we just lay out cells inside
// it in design-unit pixels.
export function FlatSlot({ slot, urls, cells, cellTags, placeholders, }) {
    const w = slot.width;
    const h = slot.height;
    const slotIsPortrait = h >= w;
    return (React.createElement("div", { style: {
            position: "absolute",
            left: 0,
            top: 0,
            width: `${w}px`,
            height: `${h}px`,
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.12)",
            boxSizing: "border-box",
        } }, cells.map((cell, i) => {
        var _a, _b;
        const [fx, fy, fw, fh] = cell;
        const cellW = fw * w;
        const cellH = fh * h;
        const cellX = fx * w;
        const cellY = fy * h;
        const url = urls.length ? ((_a = urls[i % urls.length]) !== null && _a !== void 0 ? _a : null) : null;
        const rotatePhoto = placeholders
            ? 0
            : photoRotationFor(cellW, cellH, slotIsPortrait, (_b = cellTags[i]) !== null && _b !== void 0 ? _b : "");
        return (React.createElement(Cell, { key: i, x: cellX, y: cellY, w: cellW, h: cellH, url: url, rotatePhoto: rotatePhoto }));
    })));
}
// Single cell: a positioned div containing an <img>. overflow:hidden clips
// any image that overflows the cell when its aspect ratio doesn't match
// (object-fit: cover handles the "fill-in" semantics the legacy SVG path
// did via preserveAspectRatio="xMidYMin slice"). Photo rotation kicks in
// for landscape cells inside portrait slots (matches SheetCells.photoRotationFor).
function Cell({ x, y, w, h, url, rotatePhoto, }) {
    if (!url) {
        return (React.createElement("div", { style: {
                position: "absolute",
                left: `${x}px`,
                top: `${y}px`,
                width: `${w}px`,
                height: `${h}px`,
                background: "#ccc",
            } }));
    }
    if (rotatePhoto === 0) {
        return (React.createElement("img", { src: url, alt: "", style: {
                position: "absolute",
                left: `${x}px`,
                top: `${y}px`,
                width: `${w}px`,
                height: `${h}px`,
                objectFit: "cover",
                objectPosition: "center top",
            } }));
    }
    // Rotated: render the photo at swapped dimensions, centred on the cell,
    // then rotate around the cell centre. Outer wrapper has overflow:hidden
    // so the rotated img can't extend past the cell.
    return (React.createElement("div", { style: {
            position: "absolute",
            left: `${x}px`,
            top: `${y}px`,
            width: `${w}px`,
            height: `${h}px`,
            overflow: "hidden",
        } },
        React.createElement("img", { src: url, alt: "", style: {
                position: "absolute",
                left: `${w / 2 - h / 2}px`,
                top: `${h / 2 - w / 2}px`,
                width: `${h}px`,
                height: `${w}px`,
                objectFit: "cover",
                objectPosition: "center top",
                transform: `rotate(${rotatePhoto}deg)`,
                transformOrigin: "center center",
            } })));
}
// A cell counts as landscape past this aspect.
// PackageGraphicsBootstrapBuilder::LANDSCAPE_CELL_ASPECT carries the same
// number — keep the two in step, or the editor's placeholders stop
// predicting what the webshop does with a real photo.
export const LANDSCAPE_CELL_ASPECT = 1.2;
// Whether a cell holds a class photo rather than a portrait. The
// template's resource tag is the only signal; mirrors
// PackageGraphicsBootstrapBuilder::cellHoldsGroupPhoto.
export function cellHoldsGroupPhoto(cellTag) {
    return cellTag.includes("group");
}
export function photoRotationFor(cellW, cellH, slotIsPortrait, cellTag) {
    // A class photo is already landscape, so turning it to fit a landscape
    // cell would stand it on its side. The rule is here for portraits.
    if (cellHoldsGroupPhoto(cellTag))
        return 0;
    const cellIsLandscape = cellW / Math.max(cellH, 1) > LANDSCAPE_CELL_ASPECT;
    return slotIsPortrait && cellIsLandscape ? 90 : 0;
}
